import { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Eye, Trash2, Copy, QrCode } from 'lucide-react';
import { toast } from 'sonner';

const HoverCopyCell = ({ value, displayValue, label }) => {
  const [showFull, setShowFull] = useState(false);
  if (!value) return <span className="text-sm text-slate-400">-</span>;
  return (
    <div className="relative group" onMouseEnter={() => setShowFull(true)} onMouseLeave={() => setShowFull(false)}>
      <span className="text-sm font-mono text-slate-600 cursor-default">{displayValue}</span>
      {showFull && (
        <div className="absolute z-20 left-0 top-full mt-1 bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg flex items-center gap-2 whitespace-nowrap">
          <span className="font-mono">{value}</span>
          <button
            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(value); toast.success(`${label} copied!`); }}
            className="p-0.5 hover:bg-slate-700 rounded"
            data-testid={`copy-${label?.toLowerCase()}-${value}`}
          >
            <Copy className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

const STATUS_STYLES = {
  'New Bank': 'bg-sky-100 text-sky-800 border-sky-200',
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'In Process': 'bg-amber-100 text-amber-800 border-amber-200',
  'Unassigned': 'bg-teal-100 text-teal-800 border-teal-200',
  'Inactive': 'bg-slate-100 text-slate-800 border-slate-200',
  'Freezed - Transaction': 'bg-red-100 text-red-800 border-red-200',
  'Freezed - CBI': 'bg-red-100 text-red-800 border-red-200',
  'Return': 'bg-orange-100 text-orange-800 border-orange-200',
  'Closed': 'bg-purple-100 text-purple-800 border-purple-200'
};

const maskAccount = (acc) => {
  if (!acc) return '-';
  return 'XXXXXX' + acc.toString().slice(-4);
};

const BankTable = ({ banks, isAdmin, checkPricingMissing, onViewBank, onDeleteBank }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-t border-b bg-slate-50">
            <th className="text-left p-3 text-xs font-semibold text-slate-600">Company Name</th>
            <th className="text-left p-3 text-xs font-semibold text-slate-600">Account</th>
            <th className="text-left p-3 text-xs font-semibold text-slate-600">UPI</th>
            <th className="text-left p-3 text-xs font-semibold text-slate-600">Account Type</th>
            <th className="text-left p-3 text-xs font-semibold text-slate-600">QR</th>
            <th className="text-left p-3 text-xs font-semibold text-slate-600">Branch</th>
            <th className="text-left p-3 text-xs font-semibold text-slate-600">Status</th>
            <th className="text-right p-3 text-xs font-semibold text-slate-600">Actions</th>
          </tr>
        </thead>
        <tbody>
          {banks.map((bank) => (
            <tr key={bank.id} className={`border-b hover:bg-slate-50 ${isAdmin && checkPricingMissing(bank) ? 'bg-amber-50/30' : ''}`} data-testid={`bank-row-${bank.id}`}>
              <td className="p-3">
                <div className="font-medium text-sm">{bank.companyName || bank.bankName}</div>
                {bank.companyName && <div className="text-xs text-slate-500">{bank.bankName}</div>}
              </td>
              <td className="p-3">
                <HoverCopyCell value={bank.accountNumber} displayValue={maskAccount(bank.accountNumber)} label="Account" />
              </td>
              <td className="p-3">
                <HoverCopyCell value={bank.upi} displayValue={bank.upi ? '***' + bank.upi.slice(-6) : '-'} label="UPI" />
              </td>
              <td className="p-3 text-sm text-slate-600">{bank.accountType || 'Savings'}</td>
              <td className="p-3">
                {bank.qrAvailable === 'yes' ? (
                  <div className="inline-flex items-center gap-1">
                    <QrCode className="w-4 h-4 text-green-600" />
                    <span className="text-xs text-green-700 font-medium">Yes</span>
                    {bank.qrDataUrl && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(bank.qrDataUrl); toast.success('QR copied!'); }}
                        className="inline-flex items-center gap-1 text-xs bg-slate-800 text-white px-2 py-0.5 rounded ml-1 hover:bg-slate-900"
                        data-testid={`copy-qr-${bank.id}`}
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400">No</span>
                )}
              </td>
              <td className="p-3 text-xs text-slate-600">{bank.assignedPartnerName || <span className="text-slate-400">-</span>}</td>
              <td className="p-3">
                <Badge className={STATUS_STYLES[bank.status] || 'bg-slate-100 text-slate-800'}>{bank.status}</Badge>
              </td>
              <td className="p-3 text-right">
                <div className="flex justify-end gap-1">
                  <Button onClick={() => onViewBank(bank)} variant="outline" size="sm" data-testid={`view-bank-${bank.id}`} className="gap-1">
                    <Eye className="w-3 h-3" /> View
                  </Button>
                  {isAdmin && (
                    <Button onClick={() => onDeleteBank(bank.id)} variant="outline" size="sm" data-testid={`delete-bank-${bank.id}`} className="gap-1 text-red-600 hover:text-red-700">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {banks.length === 0 && <div className="p-8 text-center text-slate-500">No banks match the filters</div>}
    </div>
  );
};

export default BankTable;
